/**
 * usePurchaseInvoiceDraft Hook
 * Alış faturası taslak kaydetme/yükleme işlemleri
 */

import { useState, useCallback, useEffect } from 'react';
import { Account, InvoiceItem } from '../components/types/invoice.types';
import { setFormDirty } from '../../../../utils/formLeaveGuard';

const DRAFT_STORAGE_KEY_PREFIX = 'purchaseInvoiceDraft';
const DRAFT_EXPIRY_DAYS = 7;

export interface PurchaseInvoiceDraftData {
  selectedAccount: Account | null;
  invoiceDate: string;
  dueDate: string;
  selectedItems: InvoiceItem[];
  notes: string;
  currency: string;
  exchangeRate: string;
  tevkifatRate: string;
  tevkifatAmount: number;
  tevkifatType: string;
  showTevkifat: boolean;
  stopajRate: number;
  stopajAmount: number;
  showStopaj: boolean;
  originalInvoiceTotal: string;
  savedAt: string;
  timestamp?: number;
}

interface UsePurchaseInvoiceDraftProps {
  companyId: string | null;
  editingInvoiceId: string | null;
  /** Taslak anahtarını ayrıştırmak için mod (fatura eski anahtarı korur) */
  mode?: 'invoice' | 'offer' | 'order';
}

export const usePurchaseInvoiceDraft = ({ companyId, editingInvoiceId, mode = 'invoice' }: UsePurchaseInvoiceDraftProps) => {
  const [isDraftModalOpen, setIsDraftModalOpen] = useState<boolean>(false);
  const [draftData, setDraftData] = useState<PurchaseInvoiceDraftData | null>(null);
  const [showDraftSavedAlert, setShowDraftSavedAlert] = useState<boolean>(false);

  const draftKeySuffix = mode === 'invoice' ? '' : `_${mode}`;
  
  /**
   * Taslağı LocalStorage'a kaydet
   */
  const saveDraftToLocalStorage = useCallback((data: {
    selectedAccount: Account | null;
    invoiceDate: string;
    dueDate: string;
    selectedItems: InvoiceItem[];
    notes: string;
    currency: string;
    exchangeRate: string;
    tevkifatRate: string;
    tevkifatAmount: number;
    tevkifatType: string;
    showTevkifat: boolean;
    stopajRate: number;
    stopajAmount: number;
    showStopaj: boolean;
    originalInvoiceTotal: string;
  }, silent: boolean = false) => {
    if (!companyId) return;
    
    const draftKey = `${DRAFT_STORAGE_KEY_PREFIX}_${companyId}${draftKeySuffix}`;
    const draft: PurchaseInvoiceDraftData = {
      ...data,
      savedAt: new Date().toISOString(),
      timestamp: Date.now(),
    };
    
    try {
      localStorage.setItem(draftKey, JSON.stringify(draft));
      if (silent) return;
      console.log('💾 [DRAFT] Taslak kaydedildi:', draftKey);
      
      setShowDraftSavedAlert(true);
      setTimeout(() => setShowDraftSavedAlert(false), 3000);
    } catch (error) {
      console.error('❌ [DRAFT] Taslak kaydedilemedi:', error);
    }
  }, [companyId, draftKeySuffix]);
  
  /**
   * Taslağı LocalStorage'dan yükle
   */
  const loadDraftFromLocalStorage = useCallback((): PurchaseInvoiceDraftData | null => {
    if (!companyId) return null;
    
    const draftKey = `${DRAFT_STORAGE_KEY_PREFIX}_${companyId}${draftKeySuffix}`;
    
    try {
      const storedDraft = localStorage.getItem(draftKey);
      if (!storedDraft) return null;
      
      const draft: PurchaseInvoiceDraftData = JSON.parse(storedDraft);
      
      // Süre kontrolü
      if (draft.timestamp) {
        const draftAge = Date.now() - draft.timestamp;
        const expiryTime = DRAFT_EXPIRY_DAYS * 24 * 60 * 60 * 1000;
        
        if (draftAge > expiryTime) {
          console.log('⚠️ [DRAFT] Taslak süresi dolmuş, siliniyor');
          clearDraftFromLocalStorage();
          return null;
        }
      }
      
      console.log('✅ [DRAFT] Taslak yüklendi');
      return draft;
    } catch (error) {
      console.error('❌ [DRAFT] Taslak yüklenirken hata:', error);
      return null;
    }
  }, [companyId]);
  
  /**
   * Taslağı LocalStorage'dan sil
   */
  const clearDraftFromLocalStorage = useCallback(() => {
    if (!companyId) return;
    
    const draftKey = `${DRAFT_STORAGE_KEY_PREFIX}_${companyId}${draftKeySuffix}`;
    try {
      localStorage.removeItem(draftKey);
      console.log('🗑️ [DRAFT] Taslak silindi:', draftKey);
    } catch (error) {
      console.error('❌ [DRAFT] Taslak silinemedi:', error);
    }
    // Başarılı kayıt sonrası ayrılma onayı sorulmasın
    setFormDirty(false);
  }, [companyId, draftKeySuffix]);
  
  /**
   * Taslak verisini forma uygula (callback'ler ile)
   */
  const applyDraftToForm = useCallback((
    draft: PurchaseInvoiceDraftData,
    setters: {
      setSelectedAccount: (acc: Account | null) => void;
      setInvoiceDate: (date: string) => void;
      setDueDate: (date: string) => void;
      setSelectedItems: (items: InvoiceItem[]) => void;
      setNotes: (notes: string) => void;
      setCurrency: (currency: string) => void;
      setExchangeRate: (rate: string) => void;
      setTevkifatRate: (rate: string) => void;
      setTevkifatAmount: (amount: number) => void;
      setTevkifatType: (type: string) => void;
      setShowTevkifat: (show: boolean) => void;
      setStopajRate: (rate: number) => void;
      setStopajAmount: (amount: number) => void;
      setShowStopaj: (show: boolean) => void;
      setOriginalInvoiceTotal: (total: string) => void;
    }
  ) => {
    try {
      console.log('📂 [DRAFT] Taslak forma uygulanıyor...');
      
      setters.setSelectedAccount(draft.selectedAccount);
      setters.setInvoiceDate(draft.invoiceDate);
      setters.setDueDate(draft.dueDate || '');
      setters.setSelectedItems(draft.selectedItems || []);
      setters.setNotes(draft.notes || '');
      setters.setCurrency(draft.currency || 'TRY');
      setters.setExchangeRate(draft.exchangeRate || '1');
      setters.setTevkifatRate(draft.tevkifatRate || "0");
      setters.setTevkifatAmount(draft.tevkifatAmount || 0);
      setters.setTevkifatType(draft.tevkifatType || "");
      setters.setShowTevkifat(draft.showTevkifat || false);
      setters.setStopajRate(draft.stopajRate || 0);
      setters.setStopajAmount(draft.stopajAmount || 0);
      setters.setShowStopaj(draft.showStopaj || false);
      setters.setOriginalInvoiceTotal(draft.originalInvoiceTotal || '');
      
      console.log('✅ [DRAFT] Taslak başarıyla uygulandı');
      setIsDraftModalOpen(false);
      setDraftData(null);
    } catch (error) {
      console.error('❌ [DRAFT] Taslak uygulanamadı:', error);
    }
  }, []);
  
  /**
   * Sayfa açıldığında taslak kontrolü
   */
  useEffect(() => {
    if (!companyId || editingInvoiceId) return;
    
    const draft = loadDraftFromLocalStorage();
    
    if (draft) {
      console.log('📂 [DRAFT] Kaydedilmiş taslak bulundu:', draft.savedAt);
      setDraftData(draft);
      setIsDraftModalOpen(true);
    }
  }, [companyId, editingInvoiceId, loadDraftFromLocalStorage]);
  
  return {
    isDraftModalOpen,
    setIsDraftModalOpen,
    draftData,
    setDraftData,
    showDraftSavedAlert,
    setShowDraftSavedAlert,
    saveDraftToLocalStorage,
    loadDraftFromLocalStorage,
    clearDraftFromLocalStorage,
    applyDraftToForm,
  };
};
